import { IPage } from '../../types';
import { useController, UseFormReturn } from 'react-hook-form';
import { FC } from 'react';
import { TranslationComponent, useTranslation } from '@pypestream/translations';
import {
  BlockList,
  Grid,
  GridCell,
  Input,
  Spacer,
  TextCaption,
  TextSubtitle,
  TextTitle,
} from '@pypestream/design-system';

type ITeamDetailsProps = IPage & {
  form: UseFormReturn<TeamInfoDetailsFormValues>;
  canManageAccess: boolean;
};

export type TeamInfoDetailsFormValues = {
  name: string;
  description: string;
};

export const TeamInfo: FC<ITeamDetailsProps> = ({ form, canManageAccess }) => {
  const { t } = useTranslation();

  const { field: nameField } = useController({
    name: 'name',
    control: form.control || undefined,
  });

  const { field: descriptionField } = useController({
    name: 'description',
    control: form.control || undefined,
  });

  const formElements = {
    get name() {
      return (
        <Input
          {...form.register('name', { required: true, minLength: 1 })}
          placeholder={`${t('manager/teams:teamDetails.teamInfo.name', { defaultValue: 'Name' })}`}
          variant="outlined"
          required
          onInput={nameField.onChange}
          disabled={!canManageAccess}
        />
      );
    },
    get description() {
      return (
        <Input
          {...form.register('description', { required: false })}
          placeholder={`${t('manager/teams:teamDetails.teamInfo.description', { defaultValue: 'Description' })}`}
          variant="outlined"
          onInput={descriptionField.onChange}
          disabled={!canManageAccess}
        />
      );
    },
  };

  return (
    <form>
      <TextTitle test-id="user-details-subtitle" size="xsmall">
        Details
      </TextTitle>
      <Spacer size="large" />

      <BlockList>
        <Grid alignItems="center">
          <GridCell xsmall="12" medium="3">
            <TextSubtitle size="small" variant="secondary">
              {`${t('manager/teams:teamDetails.teamInfo.name', {
                defaultValue: 'Name',
              })}*`}
            </TextSubtitle>
          </GridCell>
          <GridCell xsmall="12" medium="9">
            <Grid alignItems="center">
              <GridCell xsmall="12" medium="12">
                {formElements.name}
              </GridCell>
            </Grid>
          </GridCell>
        </Grid>

        <Grid alignItems="center">
          <GridCell xsmall="12" medium="3">
            <TextSubtitle size="small" variant="secondary">
              <TranslationComponent i18nKey="manager/teams:teamDetails.teamInfo.description">
                Description
              </TranslationComponent>
            </TextSubtitle>
          </GridCell>
          <GridCell xsmall="12" medium="9">
            {formElements.description}
          </GridCell>
        </Grid>
      </BlockList>
    </form>
  );
};
